import React, { useEffect, useCallback, useState, useMemo } from 'react';
import LayoutWithMembershipGuard from './components/Layout/LayoutWithMembershipGuard';
import {
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import Layout from './components/Layout/Layout';
import _, { isArray, isEmpty } from 'lodash';
import { Spin } from 'antd';
import { ConfigProvider } from 'antd';
import useCssVariables from './hooks/useCssVariables';
import {
  setIsOtpAuthCheckModalOpen,
  updateConfig,
} from './store/config';
import { useDispatch, useSelector } from 'react-redux';
import he from 'antd/es/locale/he_IL';
import en from 'antd/es/locale/en_US';
import ru from 'antd/es/locale/ru_RU';
import uk from 'antd/es/locale/uk_UA';
import fr from 'antd/es/locale/fr_FR';
import es from 'antd/es/locale/es_ES';
import pt from 'antd/es/locale/pt_PT';
import ja from 'antd/es/locale/ja_JP';
import ko from 'antd/es/locale/ko_KR';
import { RootState } from './store/store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './assets/fonts/Heebo/heebo.css';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import useSiteLang from './hooks/useSiteLang';
import useAppConfig from './hooks/useAppConfig';
import { useLoadAgentUser } from './hooks/useLoadAgentUser';
import ShoppingCartV2 from './modules/ShoppingCart/ShoppingCartV2';
import AcceptCookies from './components/AcceptCookies/AcceptCookies';
import AccessibilityPlugin from './utils/definedScripts/accessibilityPlugin';
import { FaArrowUp } from 'react-icons/fa';
import useMembershipAuthHelpers from './hooks/useMembershipAuthHelpers';
import useHandleMessagesFromBO from './useHandleMessagesFromBO';
import ErrorThrower from '@skygroup/shared/components/ErrorBoundary/ErrorThrower';
import DynamicPageEdit from './components/DynamicPages/dynamicPageEdit';
import ScrollToTop from './utils/ScrollToTop';
import { DynamicPageType } from './interfaces';
import useMembershipInit from './hooks/useMembershipInit';

const ANT_LOCALES = {
  en: en,
  he: he,
  ru: ru,
  uk: uk,
  fr: fr,
  es: es,
  pt: pt,
  ja: ja,
  ko: ko,
};

type DynamicRoutesTypes = {
  key: string;
  path: string;
  type?: DynamicPageType;
  isEdit?: boolean;
};

const dynamicRoutes: DynamicRoutesTypes[] = [
  {
    key: 'dynamicPagetype1',
    path: 'prm/:secret',
    type: DynamicPageType.PROMOTION,
  },
  {
    key: 'dynamicPagetype2',
    path: 'dst/:secret',
    type: DynamicPageType.DESTINATION,
  },
  {
    key: 'dynamicPagetype3',
    path: 'page/:secret',
    type: DynamicPageType.GENERAL,
  },
  {
    key: 'dynamicPageEdit',
    path: 'dynamicPageEdit',
    isEdit: true,
  },
];

const queryClient = new QueryClient();

function App() {
  const dispatch = useDispatch();
  const search = useSelector((state: RootState) => state.search);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAgentLoading } = useLoadAgentUser();
  const {
    config,
    agentInfo,
    currentLang,
    translations: t,
  } = useSelector((data: RootState) => data.config);
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);

  const isPreviewMode = search?.searchParams?.prvmd === '1';
  const isEditMode = searchParams.get('editMode') === 'true';

  // Load app configuration
  const { isConfigLoaded, configError } = useAppConfig();

  const { isAuthenticated } = useMembershipAuthHelpers();

  const { isTranLoaded, direction } = useSiteLang({
    siteConfig: config?.siteConfig,
  });

  // init membership
  useMembershipInit({
    isTranLoaded,
    isConfigLoaded,
    isAuthenticated,
  });

  useEffect(() => {
    try {
      const clickId = searchParams.get('click_id');
      if (clickId) {
        sessionStorage.setItem('click_id', clickId);
      }
    } catch (e) {
      // Fail silently if sessionStorage is unavailable
      console.error('Failed to store click_id in sessionStorage', e);
    }
  }, [searchParams]);

  const { selectedInstance, setSelectedInstance } = useHandleMessagesFromBO();

  useEffect(() => {
    if (
      isConfigLoaded &&
      config?.siteConfig?.membership?.auth_type === 'org_istadrut' &&
      !isAuthenticated &&
      location.pathname !== '/order/manage'
    ) {
      dispatch(setIsOtpAuthCheckModalOpen(true));
    }
  }, [isConfigLoaded, config, isAuthenticated, location, dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 6000) {
        setShowBackToTopButton(true);
      } else {
        setShowBackToTopButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const generateCssVariables = useCssVariables();

  let topClass = config?.siteConfig?.style?.topClass
      ? config?.siteConfig?.style?.topClass
      : '';

  if (agentInfo?.agentMode) {
    topClass += ' agent-mode';
  }

  const antLocale = useMemo(() => {
    if (!currentLang || !currentLang?.split('_')[0]) {
      return he;
    }
    return ANT_LOCALES[currentLang?.split('_')[0] as keyof typeof ANT_LOCALES];
  }, [currentLang]);

  return (
      <ErrorThrower error={configError}>
        <SpinLoader
            isLoading={!(isConfigLoaded && isTranLoaded) || isAgentLoading}
        >
          <QueryClientProvider client={queryClient}>
            <div
                className={`App direction-${direction} ${topClass}`}
                id="App"
                dir={direction}
            >
              {config?.siteConfig?.cssStyle ? <style
                  dangerouslySetInnerHTML={{
                    __html: config?.siteConfig?.cssStyle,
                  }}
              /> : null}
              <ScrollToTop />
              {generateCssVariables('body', config?.theme)}
              {!(
                  isPreviewMode ||
                  isEditMode ||
                  location.pathname.split('/')[1] === 'checkout' ||
                  location.pathname === '/checkout' ||
                  location.pathname.split('/')[1] === 'order' ||
                  location.pathname === '/order'
              ) && <ShoppingCartV2 pageType="rest" />}

              {showBackToTopButton && (
                  <div
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="back-to-top"
                  >
                    <FaArrowUp />
                  </div>
              )}

              <ConfigProvider direction={direction} locale={antLocale || he}>
                <Routes>

                  {dynamicRoutes.map((route) => (
                      <Route
                          key={route.key}
                          path={route.path}
                          element={
                            <DynamicPageEdit
                                isPreviewMode={isPreviewMode}
                                selectedInstance={selectedInstance}
                                setSelectedInstance={setSelectedInstance}
                                isEdit={route.isEdit}
                                type={route.type}
                            />
                          }
                      />
                  ))}

                  {config?.pages?.map((page, i) => {
                    const pathArray: string[] = isArray(page.path)
                        ? page.path
                        : [page.path];
                    return pathArray.map((path, index) => {
                      return (
                        <Route
                        key={index + path}
                        path={page.name === 'NotFound404' ? '*' : path}
                        element={
                          <LayoutWithMembershipGuard
                            pageId={page.id || page.name}
                            pagePath={path}
                            pageComponents={page.modules}
                            layout={page.layout}
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                            page={page}
                          />
                        }
                      />
                      );
                    });
                  })}
                </Routes>
                {isEmpty(config) && <Spin className="center" size="large" />}
              </ConfigProvider>
            </div>
            {!isEditMode && config?.siteConfig?.accessibility?.siteKey ? (
                <AccessibilityPlugin
                    siteKey={config?.siteConfig?.accessibility?.siteKey}
                />
            ) : null}
            <AcceptCookies />
          </QueryClientProvider>
        </SpinLoader>
      </ErrorThrower>
  );
}

export default App;
